import {
  TimelineContent,
  TimelineDate,
  TimelineHeader,
  TimelineIndicator,
  TimelineItem,
} from "@repo/ui/components/reui/timeline";
import { buttonVariants } from "@repo/ui/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui/components/ui/tooltip";
import { Link } from "@tanstack/react-router";
import { CirclePlus, User, UserSearch } from "lucide-react";
import { memo } from "react";
import { type ActivityEvent, PRIORITY_LABEL } from "@/features/tasks/lib/task-activity";
import { TASK_STATUS_LABELS, TaskStatusIcon } from "@/features/tasks/lib/task-status";
import { formatDateTime, formatRelativeTime } from "@/shared/lib/date-formats";
import { TaskPriorityIcon } from "@/shared/status/task-priority-icon";

function EventIcon({ event }: { event: ActivityEvent }) {
  switch (event.kind) {
    case "created":
      return <CirclePlus className="size-3.5 text-muted-foreground/60" />;
    case "status-change":
      return <TaskStatusIcon status={event.newValue} size={14} />;
    case "owner-change":
      return <User className="size-3.5 text-muted-foreground/60" />;
    case "priority-change":
      return <TaskPriorityIcon priority={event.newValue} size={14} />;
    case "registration-review":
      return <UserSearch className="size-3.5 text-warning" />;
    default:
      return <CirclePlus className="size-3.5 text-muted-foreground/40" />;
  }
}

function eventDescription(event: ActivityEvent): React.ReactNode {
  switch (event.kind) {
    case "created":
      return "Task created";
    case "status-change": {
      // Undefined → "draft" (the canonical initial status) so the label map
      // always renders. Avoids ad-hoc string fallbacks with mismatched casing.
      const from = TASK_STATUS_LABELS[event.oldValue ?? "draft"];
      const to = TASK_STATUS_LABELS[event.newValue ?? "draft"];
      return (
        <>
          Status changed from <span className="font-medium text-foreground">{from}</span> to{" "}
          <span className="font-medium text-foreground">{to}</span>
        </>
      );
    }
    case "owner-change": {
      if (!event.newValue) return "Unassigned";
      const name = event.newDisplay ?? event.newValue;
      return (
        <>
          Assigned to <span className="font-medium text-foreground">{name}</span>
        </>
      );
    }
    case "priority-change": {
      // Undefined → "routine" (FHIR's "no priority") so the label map always
      // renders ("None"). Avoids ad-hoc string fallbacks with mismatched casing.
      const from = PRIORITY_LABEL[event.oldValue ?? "routine"];
      const to = PRIORITY_LABEL[event.newValue ?? "routine"];
      return (
        <>
          Priority changed from <span className="font-medium text-foreground">{from}</span> to{" "}
          <span className="font-medium text-foreground">{to}</span>
        </>
      );
    }
    case "registration-review":
      return (
        <div className="flex flex-col items-start gap-2">
          <span>This registration may match an existing patient.</span>
          {event.taskId && (
            <Link
              to="/tasks/$taskId/review"
              params={{ taskId: event.taskId }}
              className={buttonVariants({ size: "xs" })}
            >
              Review duplicate
            </Link>
          )}
        </div>
      );
    default:
      return "Updated";
  }
}

export const ActivityEventItem = memo(
  ({ event, step }: { event: ActivityEvent; step: number }) => (
    <TimelineItem
      step={step}
      className="has-[+[data-completed]]:[&_[data-slot=timeline-separator]]:bg-foreground/20 group-data-[orientation=vertical]/timeline:not-last:pb-0"
    >
      <TimelineHeader className="flex items-center gap-2.5">
        <div
          aria-hidden
          className="absolute -left-6 -translate-x-1/2 top-7 -bottom-1 w-px bg-border group-last/timeline-item:hidden"
        />
        <TimelineIndicator className="size-5 border-none bg-transparent">
          <div className="flex size-5 items-center justify-center">
            <EventIcon event={event} />
          </div>
        </TimelineIndicator>
        <TimelineDate className="text-muted-foreground/60 mb-0 text-xs uppercase">
          {event.timestamp ? (
            <Tooltip>
              <TooltipTrigger>{formatRelativeTime(event.timestamp)}</TooltipTrigger>
              <TooltipContent>{formatDateTime(event.timestamp)}</TooltipContent>
            </Tooltip>
          ) : (
            ""
          )}
        </TimelineDate>
      </TimelineHeader>
      <TimelineContent className="text-muted-foreground text-xs">{eventDescription(event)}</TimelineContent>
    </TimelineItem>
  ),
  (prev, next) =>
    prev.event.timestamp === next.event.timestamp &&
    prev.event.kind === next.event.kind &&
    prev.event.oldValue === next.event.oldValue &&
    prev.event.newValue === next.event.newValue &&
    prev.event.taskId === next.event.taskId,
);
